import { Flex, ResponsiveValue, Text } from '@chakra-ui/react';
import {
  ButtonIcon,
  FileUpload,
  FormFeedback,
  Link
} from '@morressier/alembic';
import { FieldLabel } from '../Field';
import { AxiosProgressEvent } from 'axios';
import {
  FormFieldFileUpload,
  FormFieldInfoText
} from '@publishing-engine/domain/types/article';
import axiosInstance from 'src/api/client';
import {
  downloadFile,
  getFileExtension,
  getReferenceName,
  parseUploadError
} from 'src/utils/file';
import { get } from 'lodash';
import { useSubmissionId } from '../../hooks/useSubmissionId';
import {
  deleteUploadedFile,
  getSignedPrivateAccessUrl,
  getSignedPrivateUploadUrl
} from '../../api/queries';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { IFileUploaded } from './common/types';
import { FieldError, useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import RichTextPreview from '../Submission/GenericFieldComponents/RichText/RichTextPreview';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks';
import { useDuplicateConfirm } from 'src/hooks/useDuplicateConfirm';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';

interface IFormFieldSingleFileUploadProps {
  config: Omit<FormFieldFileUpload, 'component'>;
  methods: any;
  submissionContextId: string;
  referenceName?: string;
  isEditingAllowed?: boolean;
  submissionId?: string;
  width?: ResponsiveValue<string>;
  TitlePageSampleDownload?: FormFieldInfoText | null | undefined;
  currentWorkflowState?: string;
  submissionFields?: ISubmissionFieldsResponse | unknown;
}

export const getAllowedExtensions = (
  allowedFileFormats: Record<string, string[]>
): string[] => {
  const extensions = Object.values(allowedFileFormats)
    .flat()
    .map(ext => ext.replace('.', '').toUpperCase());

  // Remove duplicates
  return Array.from(new Set(extensions));
};

const FormFieldSingleFileUpload: React.FC<IFormFieldSingleFileUploadProps> = ({
  config,
  referenceName,
  submissionId: staticSubmissionId,
  submissionFields,
  isEditingAllowed = true,
  width = '100%',
  TitlePageSampleDownload
}) => {
  const {
    setError,
    clearErrors,
    formState: { errors },
    register,
    setValue,
    watch,
    getValues
  } = useFormContext();
  const {
    caseDataFields: { main: field },
    params: { labels, validations },
    name
  } = config;

  const { checkIsDuplicateFile, duplicateFileModal } = useDuplicateConfirm();
  const error = errors[field];
  const mainError = get(errors, field) as FieldError;
  const file = watch(field);

  const submissionId = staticSubmissionId ?? useSubmissionId();
  const getUploadUrl = (extension: string) => {
    if (!submissionId) {
      throw new Error('FormFieldSingleFileUpload: no submission id. ' + field);
    }
    return getSignedPrivateUploadUrl(submissionId, extension);
  };

  const handleOpenFile = async (url: string) => {
    if (!submissionId) {
      return;
    }
    const signedUrl = await getSignedPrivateAccessUrl(submissionId, url);
    window.open(signedUrl, '_blank');
  };

  const uploadFile = async (file: File) => {
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: IFileUploaded = {
      name: file.name,
      referenceName: referenceName || getReferenceName(file.name),
      progress: 0,
      isUploading: true,
      extension: fileExtension,
      url: ''
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };

      setValue(field, fileToUpload);
    };

    try {
      setValue(field, fileToUpload);

      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);

      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        extension: fileExtension
      };

      setValue(field, fileToUpload);
    } catch (e) {
      const message = parseUploadError(e);
      setError(field, {
        type: 'upload-error',
        message
      });
    } finally {
      setValue(field, {
        ...fileToUpload,
        isUploading: false
      });
    }
  };

  const removeFile = async (fileId?: string) => {
    validations.required === false
      ? setValue(field, null)
      : setValue(field, {
          name: '',
          url: '',
          referenceName: '',
          extension: ''
        });
    fileId && deleteUploadedFile(fileId);
  };

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    clearErrors(field);

    const selected = acceptedFiles[0];
    const fields =
      typeof submissionFields === 'object' && submissionFields !== null
        ? {
            ...submissionFields,
            [name]: getValues()?.fileSet ?? getValues()
          }
        : getValues()?.fileSet ?? getValues();
    const canUpload = await checkIsDuplicateFile(
      { fieldName: field, name: selected?.name || '' },
      fields
    );
    if (!canUpload) return;
    removeFile(file?.fileId);
    selected && (await uploadFile(selected));
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] = rejectedFiles => {
    if (rejectedFiles[0]) {
      const rejectedFileError = rejectedFiles[0].errors[0];
      setValue(field, {
        name: rejectedFiles[0].file.name,
        url: '',
        isUploading: false,
        progress: 0,
        referenceName: '',
        extension: ''
      });
      if (rejectedFileError?.code === 'file-invalid-type') {
        setError(field, {
          type: 'upload-error',
          message: 'An unsupported file format selected.'
        });
      }
      if (rejectedFileError?.code === ErrorCode.FileTooLarge) {
        setError(field, {
          type: 'upload-error',
          message: `File is larger than ${validations.maxSizeMB}MB`
        });
      }
      if (rejectedFileError?.code === ErrorCode.TooManyFiles) {
        setError(field, {
          type: 'upload-error',
          message: rejectedFileError.message
        });
      }
    }
  };
  return (
    <>
      <Flex flexDirection="column" gap={2} width="100%">
        {isEditingAllowed && (
          <Flex flexDirection="column" gap={2}>
            <Flex flexDirection="column">
              <FieldLabel
                isRequired={validations.required}
                label={labels.main}
                helpText={labels.helpText}
                subText={
                  <>
                    {parseStringWithLinks(labels.subtitle)}
                    {TitlePageSampleDownload && (
                      <>
                        <Text textStyle={'textContext'}>
                          <RichTextPreview
                            value={TitlePageSampleDownload.params.labels.main}
                          />

                          {(() => {
                            const match =
                              TitlePageSampleDownload.params.labels.subtitle.match(
                                /\[([^\]]+)\]\(([^)]+)\)/
                              );
                            if (!match) return null;
                            const linkText = match[1];
                            const linkUrl = match[2];

                            // Ensure linkUrl exists before calling openFile
                            if (!linkUrl) return null;

                            return (
                              <Link
                                ml={1}
                                onClick={() => downloadFile(linkUrl)}
                                variant="secondary"
                              >
                                <Text
                                  maxWidth="100%"
                                  whiteSpace="normal"
                                  overflowWrap="anywhere"
                                  fontWeight="bold"
                                >
                                  {linkText}
                                </Text>
                              </Link>
                            );
                          })()}
                        </Text>
                      </>
                    )}
                  </>
                }
              />
            </Flex>
            <Flex flexDirection="column" w={width} bgColor="surfacePrimaryFill">
              <FileUpload
                {...register(field)}
                multiple={false}
                uploadButtonText="Select file"
                onDrop={onDrop}
                onDropRejected={onDropRejected}
                maxSize={validations.maxSizeMB * 1000000}
                isInvalid={Boolean(error?.message || mainError)}
                accept={validations.allowedFileFormats}
              />
            </Flex>
            <Text textStyle="textContext" color="textSecondary">
              Accepted format
              {getAllowedExtensions(validations.allowedFileFormats).length > 1
                ? '(s)'
                : ''}
              :{' '}
              {getAllowedExtensions(validations.allowedFileFormats).join(', ')}.
              Max size: {validations.maxSizeMB}MB
            </Text>
          </Flex>
        )}

        {!isEditingAllowed && (
          <FieldLabel
            isRequired={validations.required}
            label="Attached File"
            subText=""
            helpText={labels.helpText}
          />
        )}
        {file ? (
          <>
            {file.url && (
              <Flex gap={1}>
                <FormFeedback variant="success">
                  <a
                    onClick={() => handleOpenFile(file.url)}
                    style={{
                      cursor: 'pointer',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      width: '100%'
                    }}
                  >
                    <Text textStyle="textBody">{file.name}</Text>
                  </a>
                </FormFeedback>
                {!file.isUploading && (
                  <ButtonIcon
                    size="small"
                    ariaLabel={`${file.name} delete`}
                    onClick={() => removeFile(file?.fileId)}
                    icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  />
                )}
              </Flex>
            )}
            {file?.isUploading ? (
              <Text textStyle="textContext">Uploading... {file.progress}%</Text>
            ) : mainError?.message ? (
              <FormFeedback variant="error">{mainError.message}</FormFeedback>
            ) : (
              !file?.url &&
              mainError && (
                <FormFeedback variant="error">
                  Please select a file to upload.
                </FormFeedback>
              )
            )}
          </>
        ) : (
          !file &&
          mainError && (
            <FormFeedback variant="error">
              Please select a file to upload.
            </FormFeedback>
          )
        )}
      </Flex>
      {duplicateFileModal}
    </>
  );
};

export default FormFieldSingleFileUpload;
